
<template>
  <div class="wrapper" :style="{ backgroundImage: bgImageUrl ? `url(${bgImageUrl})` : '' }">
    <div class="content-wrapper step-2">
      <div class="step-wrapper">
        <div class="step">Modification de votre page personnalisée</div>
        <div class="form-wrapper">
          <p class="explanations">
            La carte ci-dessous représente votre campagne dans l’espace des talents. <br>
            Vous pouvez la personnaliser comme vous le souhaitez et aller plus loin avec l’icône double flèche. 
          </p>
          <div class="campaign-layout">
            <CampaignCard
              data-step="18"
              :data="state.publicCampaignData"
              @changeImage="prepareUploadHeaderImage"
              @changePublicName="(e) => (state.publicCampaignData.public_name = e)"
              @changeJobTitle="(e) => (state.publicCampaignData.job_title = e)"
              @changeJobDescription="
                (e) => (state.publicCampaignData.job_description = e)
              "
              @toggleError="(val) => (state.publicCampaignCardError = val)"
              :mode="campaignCardMode"
              :text_colour="state.publicCampaignData.text_colour"
              :elem_colour="state.publicCampaignData.elem_colour"
              
            />
            <CampaignThemeCard
              ref="campaignThemeRef"
              @toggleTheme="toggleTooltip"
              @changeTextColour="(e) => (state.publicCampaignData.text_colour = e)"
              @changeThemeColour="(e) => (state.publicCampaignData.elem_colour = e)"
              @setHeaderImage="handleHeaderImage"
              @setBackground="handleBGSelect"
              @newBackground="handleBGUploaded"
            />
          </div>
          <div class="buttons-inline">
            <Button
              class="secondary icon icon-left no-dark-mode"
              label="Retourner aux paramètres de la campagne"
              icon="bi-chevron-left"
              @click="goBack()"
            />
            <Button
              class="icon icon-left no-dark-mode green"
              :disabled="!canGoNext"
              :loading="state.isLoading"
              label="Enregistrer les modifications effectuées"
              icon="bi-check-lg"
              @click="updateCampaign()"
              v-tooltip="{
                content: 'Tous les champs ne sont pas remplis.',
                triggers: canGoNext ? [] : ['hover'],
              }"
            />
          </div>
        </div>
      </div>
    </div>  
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useCampaignStore } from "@/stores/campaign.js";
import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";
import { useToast } from "vue-toastification";
import { useAppStore } from "@/stores/app.js";
import { useImageStore } from "@/stores/image.js";
import { getError } from "@/composables/error.js";
import { getImage, getFileFromURL } from "@/composables/getImage.js";

const appStore = useAppStore();

const toast = useToast();

// Init stores
const { uploadImage } = useImageStore();

const route = useRoute();

const router = useRouter();

const emits = defineEmits(["handleBGUpdate"]);

const campaignStore = useCampaignStore();

if (!campaignStore.currentCampaign) {
  await campaignStore.getCampaign(
    route.params.companyId,
    route.params.spaceId,
    route.params.campaignId
  );
}

const campaignCardMode = ref("new");

const campaignThemeRef = ref(null); //TODO FIX when Campaign theme has not been opened

const bgImageUrl = ref(null);

if(campaignStore.currentCampaign.bg_image_id) {
  const imageBGUrl = getImage(campaignStore.currentCampaign.bg_image_id);
  bgImageUrl.value = imageBGUrl;
}

const state = reactive({
  publicCampaignData: {...campaignStore.currentCampaign},
  isLoading: false,
  campaign_image: null,
  campaign_image_deleted: false,
  campaign_bg_image: null,
  campaign_bg_image_deleted: false,
  publicCampaignCardError: false,
  mode: "new"
});

const canGoNext = computed(() => {
  if (state.publicCampaignCardError) return false;

  const { public_name, job_title, job_description } = state.publicCampaignData;

  return public_name && job_title && job_description;
});

function goBack() {
  appStore.showCampaignLinkInfo = true;
  appStore.campaignParametersTab = 1; //preview tab
  router.push({
    name: "campaign",
    params: { 
      spaceId: route.params.spaceId,
      campaignId:  route.params.campaignId
     },
  });
}

async function updateCampaign() {
  state.isLoading = true;
  if (state.publicCampaignCardError) {
    return toast.error(
      "Les informations publiques de votre campagne ne sont pas valides, veillez à bien respecter la limite de caractères."
    );
  }

  if (state.informationsError) {
    return toast.error(
      "Le nom privé de votre campagne n'est pas valide, veuillez vérifier votre saisie"
    );
  }

  if (state.campaign_image) {
    const { image_id } = await uploadImage(
      campaignStore.currentCampaign.company_id,
      "campaign_public_image",
      state.campaign_image
    );

    if (image_id) {
      state.publicCampaignData.image_id = image_id;
    } else {
      toast.error("Une erreur est survenue lors du téléchargement de l'image");
    }
  }
  else if(state.campaign_image_deleted) {
    //There was an image now there is none
    state.publicCampaignData.image_id = null;
  }

  if (state.campaign_bg_image) {
    const { image_id } = await uploadImage(
      route.params.companyId,
      "campaign_public_bg_image",
      state.campaign_bg_image
    );

    if (image_id) {
      state.publicCampaignData.bg_image_id = image_id;
    } else {
      toast.error("Une erreur est survenue lors du téléchargement de l'image");
    }
  }
  else if(state.campaign_bg_image_deleted) {
    //There was an image now there is none
    state.publicCampaignData.bg_image_id = null;
  }
  
  const campaignObj = state.publicCampaignData;
  delete campaignObj.enabled;

  if (JSON.stringify(campaignObj) !== JSON.stringify(campaignStore.currentCampaign)) {
    const { campaign } = await campaignStore.updateCampaign(
      campaignStore.currentCampaign.company_id,
      campaignStore.currentCampaign.workspace_id,
      state.publicCampaignData
    );

    if (campaign.campaign_id) {
      toast.success("Informations sauvegardées");
    } else {
      toast.error(
        "Une erreur est survenue lors de la modification de votre campagne"
      );
    }
  }
  //Go back to campaign page with closed setting
  appStore.showCampaignLinkInfo = false;
  router.push({
    name: "campaign",
    params: { 
      spaceId: route.params.spaceId,
      campaignId:  route.params.campaignId
     },
  });
  
  state.isLoading = false;
}

function toggleTooltip(value) {
  if(value) {
    campaignCardMode.value = "new";
  }
  else {
    campaignCardMode.value = "hide";
  }
}

async function handleBGSelect(data) {
  const imageBGUrl =  data.image;
  const toUpload = data.toUpload;
  bgImageUrl.value = imageBGUrl;
  if(toUpload) {
    try {
      state.campaign_bg_image = await getFileFromURL(imageBGUrl, 'image.jpg', 'image/jpeg');
      state.campaign_bg_image_deleted = false;
    }
    catch (error) {
      console.error("Error converting URL to file:", error);
    }
  }
  else {
    //remove previous image if there was one 
    state.campaign_bg_image = null;
    // if there is no image delete previous one
    if(!imageBGUrl) {
      state.campaign_bg_image_deleted = true;
    }    
  }
  
}

// Pass the file uploaded event to the parent component
function handleBGUploaded(imgFile) {
  if(imgFile) {
    const imageBGUrl = URL.createObjectURL(imgFile);
    bgImageUrl.value = imageBGUrl;
    state.campaign_bg_image = imgFile;
    state.campaign_bg_image_deleted = false;
  }
  else {
    bgImageUrl.value = false;
    state.campaign_bg_image = null;
    state.campaign_bg_image_deleted = true;
  }
};

async function  handleHeaderImage(data)  {
  const imageId =  data.image;
  const toUpload = data.toUpload;
  state.publicCampaignData.image_id = imageId;
  
  if(imageId) {  
    if(toUpload) {  
      const imageUrl = getImage(imageId);
      try {
        state.campaign_image = await getFileFromURL(imageUrl, 'image.jpg', 'image/jpeg');
      }
      catch (error) {
        console.error("Error converting URL to file:", error);
      } 
    }
    else {      
      //remove previous image if there was one
      state.campaign_image = null;
    }
    state.campaign_image_deleted = false;
  }
  else {
    // handle image deletion
    state.campaign_image_deleted = true;
    //remove previous image if there was one
    state.campaign_image = null;
  }
};

function  prepareUploadHeaderImage(imgFile)  {
  state.publicCampaignData.image_id = 'tmp_new_id'; // Have a temp id so image can be restored
  state.campaign_image = imgFile;
  
  // Reset selection on changing header
  if (campaignThemeRef.value && campaignThemeRef.value.resetSelection) {
    campaignThemeRef.value.resetSelection(); // Call the method on the child component
  }
};

</script>
